import React, { useState, useEffect } from 'react';
import { journalAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import { 
  Edit, 
  Trash2, 
  Search, 
  Filter, 
  Calendar, 
  ChevronLeft, 
  ChevronRight, 
  Loader2,
  Eye,
  EyeOff,
  Tag,
  Smile,
  FileText,
  Image,
  Download,
  X
} from 'lucide-react';
import { format } from 'date-fns';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ConfirmDialog from '../../components/UI/ConfirmDialog';

const JournalEntryList = ({ onEditEntry, onRefresh }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    entry_type: '',
    mood: '',
    search: '',
    start_date: null,
    end_date: null,
  });
  const [isDeleting, setIsDeleting] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerImages, setViewerImages] = useState([]);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [confirmState, setConfirmState] = useState({ open: false, entryId: null });

  const entryTypeOptions = [
    { value: '', label: 'All Types' },
    { value: 'trade', label: 'Trade Analysis' },
    { value: 'strategy', label: 'Strategy Note' },
    { value: 'market', label: 'Market Observation' },
    { value: 'lesson', label: 'Lesson Learned' },
    { value: 'goal', label: 'Goal Setting' },
    { value: 'general', label: 'General Note' },
  ];

  const moodOptions = [
    { value: '', label: 'All Moods' },
    { value: 'excited', label: 'Excited' },
    { value: 'confident', label: 'Confident' },
    { value: 'neutral', label: 'Neutral' },
    { value: 'cautious', label: 'Cautious' },
    { value: 'frustrated', label: 'Frustrated' },
    { value: 'disappointed', label: 'Disappointed' },
  ];

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        type: filters.entry_type || undefined,
        mood: filters.mood || undefined,
        search: filters.search || undefined,
        date_from: filters.start_date ? format(filters.start_date, 'yyyy-MM-dd') : undefined,
        date_to: filters.end_date ? format(filters.end_date, 'yyyy-MM-dd') : undefined,
      };
      
      const response = await journalAPI.getEntries(params);
      if (response.data) {
        setEntries(response.data.results || response.data);
        setTotalPages(Math.ceil((response.data.count || response.data.length) / 10));
      }
    } catch (error) {
      console.error('Error fetching journal entries:', error);
      toast.error('Failed to load journal entries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [currentPage, filters]);

  const requestDelete = (entryId) => {
    setConfirmState({ open: true, entryId });
  };

  const confirmDelete = async () => {
    const entryId = confirmState.entryId;
    if (!entryId) return;
    setConfirmState({ open: false, entryId: null });
    setIsDeleting(entryId);
    try {
      await journalAPI.deleteEntry(entryId);
      toast.success('Entry deleted successfully!');
      fetchEntries();
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to delete entry');
    } finally {
      setIsDeleting(null);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const getEntryTypeColor = (type) => {
    const colors = {
      'trade': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'strategy': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'market': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      'goal': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
      'lesson': 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200',
      'general': 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
    };
    return colors[type] || colors['general'];
  };

  const getMoodIcon = (mood) => {
    const icons = {
      'excited': '🤩',
      'neutral': '😐',
      'cautious': '🫤',
      'frustrated': '😤',
      'confident': '😎',
      'disappointed': '🙁',
    };
    return icons[mood] || '😐';
  };

  const isImageAttachment = (att) => {
    const type = att?.content_type || att?.type;
    if (type && typeof type === 'string') {
      if (type.startsWith('image/')) return true;
    }
    const name = att?.name || '';
    return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
  };

  const openImageViewer = (attachment, attachments) => {
    const images = (attachments || []).filter(isImageAttachment);
    if (images.length === 0) {
      toast.error('No viewable images in these attachments');
      return;
    }
    const idx = Math.max(0, images.findIndex((img) => img.url === attachment.url));
    setViewerImages(images.map((img) => ({ src: img.url, title: img.name })));
    setViewerIndex(idx === -1 ? 0 : idx);
    setViewerOpen(true);
  };

  if (loading && entries.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <p className="ml-3 text-gray-600 dark:text-gray-300">Loading entries...</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Journal Entries</h2>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <Search className="inline h-4 w-4 mr-1" />
            Search
          </label>
          <input
            type="text"
            placeholder="Search entries..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <Filter className="inline h-4 w-4 mr-1" />
            Type
          </label>
          <Select
            options={entryTypeOptions}
            value={entryTypeOptions.find(opt => opt.value === filters.entry_type)}
            onChange={(selectedOption) => handleFilterChange('entry_type', selectedOption?.value || '')}
            className="dark:text-gray-900"
            classNamePrefix="react-select"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <Smile className="inline h-4 w-4 mr-1" />
            Mood
          </label>
          <Select
            options={moodOptions}
            value={moodOptions.find(opt => opt.value === filters.mood)}
            onChange={(selectedOption) => handleFilterChange('mood', selectedOption?.value || '')}
            className="dark:text-gray-900"
            classNamePrefix="react-select"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <Calendar className="inline h-4 w-4 mr-1" />
            Start Date
          </label>
          <DatePicker
            selected={filters.start_date}
            onChange={(date) => handleFilterChange('start_date', date)}
            dateFormat="yyyy-MM-dd"
            placeholderText="Start date"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            isClearable
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <Calendar className="inline h-4 w-4 mr-1" />
            End Date
          </label>
          <DatePicker
            selected={filters.end_date}
            onChange={(date) => handleFilterChange('end_date', date)}
            dateFormat="yyyy-MM-dd"
            placeholderText="End date"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            isClearable
          />
        </div>
      </div>

      {/* Entries List */}
      {entries.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">No journal entries found</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
            Start by creating your first journal entry!
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {entries.map((entry) => (
            <div key={entry.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 border border-gray-200 dark:border-gray-600 hover:shadow-md transition-shadow duration-200">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {entry.title}
                    </h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEntryTypeColor(entry.entry_type)}`}>
                      {entry.entry_type_display || entry.entry_type}
                    </span>
                    {entry.is_private && (
                      <span className="px-2 py-1 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full text-xs font-medium flex items-center">
                        <EyeOff className="h-3 w-3 mr-1" />
                        Private
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 mb-3">
                    <span className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      {format(new Date(entry.created_at), 'MMM dd, yyyy')}
                    </span>
                    {entry.trade_date && (
                      <span className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        Trade: {format(new Date(entry.trade_date), 'MMM dd, yyyy')}
                      </span>
                    )}
                    {entry.stock_symbol && (
                      <span className="flex items-center">
                        <Tag className="h-4 w-4 mr-1" />
                        {entry.stock_symbol}{entry.stock_name ? ` - ${entry.stock_name}` : ''}
                      </span>
                    )}
                    {entry.quantity != null && (
                      <span className="flex items-center">
                        <Tag className="h-4 w-4 mr-1" />
                        Qty: {entry.quantity}
                      </span>
                    )}
                    {entry.risk_reward && (
                      <span className="flex items-center">
                        <Tag className="h-4 w-4 mr-1" />
                        R:R {entry.risk_reward}
                      </span>
                    )}
                    {entry.pnl != null && (
                      <span className="flex items-center">
                        <Tag className="h-4 w-4 mr-1" />
                        P/L: {Number(entry.pnl) >= 0 ? '+' : ''}{entry.pnl}
                      </span>
                    )}
                    {entry.mood && (
                      <span className="flex items-center">
                        <Smile className="h-4 w-4 mr-1" />
                        {getMoodIcon(entry.mood)} {entry.mood_display || entry.mood}
                      </span>
                    )}
                    {entry.tags && (
                      <span className="flex items-center">
                        <Tag className="h-4 w-4 mr-1" />
                        {entry.tags}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => onEditEntry?.(entry)}
                    className="p-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 transition-colors duration-200"
                    title="Edit Entry"
                  >
                    <Edit className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => requestDelete(entry.id)}
                    className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200 transition-colors duration-200"
                    title="Delete Entry"
                    disabled={isDeleting === entry.id}
                  >
                    {isDeleting === entry.id ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Trash2 className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Content Preview */}
              {entry.content && (
                <div className="mb-4">
                  <p className="text-gray-700 dark:text-gray-300 line-clamp-3">
                    {entry.content}
                  </p>
                </div>
              )}

              {/* Attachments */}
              {entry.attachments && entry.attachments.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                    <Image className="h-4 w-4 mr-1" />
                    Attachments ({entry.attachments.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {entry.attachments.map((attachment, index) => (
                      <div key={index} className="flex items-center space-x-2 bg-white dark:bg-gray-600 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-500">
                        <Image className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{attachment.name}</span>
                        {isImageAttachment(attachment) && (
                          <button
                            onClick={() => openImageViewer(attachment, entry.attachments)}
                            className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200"
                            title="View"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        )}
                        {!isImageAttachment(attachment) && (
                          <button
                            onClick={() => window.open(attachment.url, '_blank')}
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                            title="Download"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Related Stock Info */}
              {entry.related_stock_info && (
                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">Related Stock</h4>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    {entry.related_stock_info.symbol} - {entry.related_stock_info.name}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-4 mt-8">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors duration-200"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Page {currentPage} of {totalPages}
          </span>
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors duration-200"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Image Viewer Modal */}
      {viewerOpen && viewerImages.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center">
          <button
            onClick={() => setViewerOpen(false)}
            className="absolute top-4 right-4 text-white hover:text-gray-200"
            aria-label="Close"
          >
            <X className="h-8 w-8" />
          </button>

          <button
            onClick={() => setViewerIndex((viewerIndex - 1 + viewerImages.length) % viewerImages.length)}
            className="absolute left-4 md:left-8 text-white disabled:opacity-50"
            aria-label="Previous"
            disabled={viewerImages.length <= 1}
          >
            <ChevronLeft className="h-10 w-10" />
          </button>

          <div className="max-w-[90vw] max-h-[85vh] flex flex-col items-center">
            <img
              src={viewerImages[viewerIndex]?.src}
              alt={viewerImages[viewerIndex]?.title || 'image'}
              className="object-contain max-h-[80vh] max-w-[90vw] rounded-lg shadow-2xl"
            />
            {viewerImages[viewerIndex]?.title && (
              <div className="mt-3 text-sm text-gray-100">
                {viewerImages[viewerIndex].title} ({viewerIndex + 1}/{viewerImages.length})
              </div>
            )}
          </div>

          <button
            onClick={() => setViewerIndex((viewerIndex + 1) % viewerImages.length)}
            className="absolute right-4 md:right-8 text-white disabled:opacity-50"
            aria-label="Next"
            disabled={viewerImages.length <= 1}
          >
            <ChevronRight className="h-10 w-10" />
          </button>
        </div>
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={confirmState.open}
        title="Delete entry?"
        message="This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        danger
        onConfirm={confirmDelete}
        onCancel={() => setConfirmState({ open: false, entryId: null })}
      />
    </div>
  );
};

export default JournalEntryList;